import star from '@/assets/images/favorite-star.png';
import fileSign from '@/assets/images/list-doc-icon.webp';
import {
  ITemplateConfig1,
  ITemplateConfig2,
  ITemplateDetail,
  addFavorites,
  deleteFavorites,
} from '@/services/templateCenter';
import { Button } from '@ai-indeed/idp-ui';
import { createSearchParams, useNavigate } from '@umijs/max';
import { Divider, Modal, ModalProps, Spin, message } from 'antd';
import { FC, useEffect, useState } from 'react';
import { IconFont } from '../IconFont';
import { PdfRenderer } from './PdfRenderer';
import { MODULE_TO_DETAIL } from './conts';
import './index.less';
import { downloadFile } from './utils';

export interface IProps<T> extends ModalProps {
  data: T;
  uploadArea?: React.ReactNode;
  loading?: boolean;
  pdfUrl: string;
  onChangeFavorite?: (value: boolean) => void;
  onDownload?: () => void;
  onShare?: () => void;
  onTrySample?: (taskCode: string, module: string) => void;
}
export const PreviewModal: FC<IProps<ITemplateDetail>> = (props) => {
  const {
    data,
    uploadArea,
    pdfUrl,
    centered = true,
    open = false,
    loading = false,
    footer = null,
    width = 900,
    ...restProps
  } = props;
  const navigate = useNavigate();
  const {
    template_desc = '',
    template_name = '',
    is_builtin,
    is_favorite,
    template_config = {},
  } = data || {};
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [isFavorite, setIsFavorite] = useState(is_favorite);
  const [tags, setTags] = useState<
    Array<{ tag_code: string; tag_name: string }>
  >([]);

  useEffect(() => {
    setIsFavorite(is_favorite);
  }, [is_favorite]);

  const handleFavorite = () => {
    const { onChangeFavorite } = props;
    const { template_code } = data;
    let promise;
    if (isFavorite) {
      promise = deleteFavorites(template_code);
    } else {
      promise = addFavorites(template_code);
    }
    promise.then(() => {
      setIsFavorite(!isFavorite);
      onChangeFavorite?.(!isFavorite);
    });
  };

  const handleDownload = () => {
    const { onDownload } = props;
    if (typeof onDownload === 'function') {
      onDownload();
      return;
    }
    const pdfUrl = data.preview_pdf_url;
    if (!pdfUrl) {
      message.error('文件地址不存在');
      return;
    }
    downloadFile(pdfUrl, data.template_name + '样例文件');
  };

  const handleShare = () => {
    const { onShare } = props;
    onShare?.();
  };

  const toTaskDetail = (taskCode: string) => {
    if (!taskCode) {
      return;
    }
    let pathname = '/text-detail';
    const { module, template_code } = data;
    const params: { [key: string]: string } = { 'task-code': taskCode };
    const moduleInfo = MODULE_TO_DETAIL.find((v) => v.module === module);
    if (moduleInfo) {
      pathname = moduleInfo.detail;
      if (module === 'SCENE') {
        params['template-code'] = template_code;
      }
    }
    if (pathname) {
      navigate({
        pathname,
        search: createSearchParams(params).toString(),
      });
    }
  };

  const handleTrySample = () => {
    const { onTrySample } = props;
    const { preview_task_code: taskCode, module } = data;
    if (typeof onTrySample === 'function') {
      onTrySample(taskCode, module);
      return;
    }
    if (!taskCode) {
      message.error('任务ID不存在');
      return;
    }
    toTaskDetail(taskCode);
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const { onCancel } = props;
    onCancel?.(e);
  };

  const getRandom = (min: number, max: number, list: number[] = []) => {
    let index = Math.floor(Math.random() * (max - min) + min);
    while (list.includes(index)) {
      index = Math.floor(Math.random() * (max - min) + min);
    }
    return index;
  };

  const geneRandomTags = (
    tags: Array<{ tag_code: string; tag_name: string }>,
  ) => {
    const len = tags.length;
    if (len >= 0 && len < 7) {
      return tags;
    }
    const actualTags: Array<{ tag_code: string; tag_name: string }> = [];
    const existRandom: number[] = [];
    for (let i = 0; i < 6; i++) {
      const index = getRandom(0, len, existRandom);
      existRandom.push(index);
      actualTags.push(tags[index]);
    }
    return [...actualTags];
  };

  const getTags = () => {
    if (Object.keys(template_config).length === 0) {
      return;
    }
    let tags: Array<{ tag_code: string; tag_name: string }> = [];
    if (Object.hasOwn(template_config, 'tags')) {
      tags = (template_config as ITemplateConfig1)?.tags || [];
    } else {
      tags = (
        (template_config as ITemplateConfig2)?.combined_templates || []
      ).map((v) => ({ tag_code: v.template_code, tag_name: v.template_name }));
    }
    const actualTags = geneRandomTags(tags);
    setTags(actualTags);
  };

  const toggleShowAllCategories = () => {
    setShowAllCategories((pre) => !pre);
  };

  useEffect(() => {
    if (open && template_config) {
      getTags();
    }
  }, [template_config, open, loading]);

  return (
    <Modal
      {...restProps}
      centered={centered}
      footer={footer}
      width={width}
      open={open}
      className="pm-wrapper"
      closeIcon={<IconFont type="icon-guanbi"></IconFont>}
      onCancel={handleCancel}
    >
      <div className="pm-content">
        {!loading ? (
          <>
            <div className="pm-pdf-area">
              {pdfUrl && <PdfRenderer key={pdfUrl} src={pdfUrl}></PdfRenderer>}
            </div>
            <div className="pm-operate-area">
              <div className="pm-basic-info">
                <img width={30} height={30} src={fileSign} alt="fileSign" />
                <p className="pm-basic-info__name">{template_name}</p>
                <span className="pm-basic-info__tag">
                  {is_builtin ? '内置' : '自定义'}
                </span>
              </div>

              <div className="pm-operator">
                <span className="pm-operator__item" onClick={handleFavorite}>
                  {isFavorite ? (
                    <img width={16} height={16} src={star} alt="收藏" />
                  ) : (
                    <IconFont type="icon-shoucang"></IconFont>
                  )}
                  收藏
                </span>
                <span className="pm-operator__item" onClick={handleDownload}>
                  <IconFont type="icon-xiazai2"></IconFont>下载样例
                </span>
                <span
                  className="pm-operator__item"
                  onClick={handleShare}
                  style={{ display: 'none' }}
                >
                  <IconFont type="icon-fenxiang2"></IconFont>分享
                </span>
              </div>

              <Divider></Divider>

              <div className="flex justify-between mb-6">
                <Button
                  type="secondary"
                  size="superLarge"
                  style={{ width: '148px' }}
                  onClick={handleTrySample}
                >
                  试试样例
                </Button>
                {uploadArea}
              </div>

              <div className="pm-category">
                <div className="pm-category__title">
                  <p>主要抽取点</p>
                  <span
                    onClick={toggleShowAllCategories}
                    style={{ display: 'none' }}
                  >
                    {showAllCategories ? '收起' : '更多'}
                    <IconFont
                      type={
                        showAllCategories ? 'icon-xiangshang' : 'icon-xiangxia'
                      }
                      className="ml-1 text-[#B7BAC4]"
                    ></IconFont>
                  </span>
                </div>
                <div className={`pm-category__content`}>
                  {tags.map((tag) => (
                    <div key={tag.tag_code} className="pm-tag">
                      {tag.tag_name}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pm-desc">
                <p>模板功能</p>
                <p>{template_desc}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-4/5">
            <Spin tip="loading..." delay={800} />
          </div>
        )}
      </div>
    </Modal>
  );
};
